import { useAuth } from '@/services/auth';

/**
 * The signup use-case read-back: the account bootstrap carries
 * `signup_profile` — what the user told us at signup. Wire values per the
 * 2026-05-21 taxonomy; older accounts (pre-Feb-2026) have no profile and
 * read back as "didn't answer".
 */
export const SignupUseCase = {
  AutomatedWorkflows: 'automated_workflows',
  Transactional: 'transactional',
  Newsletters: 'newsletters',
  DynamicSegmentation: 'dynamic_segmentation',
} as const;
export type SignupUseCase = (typeof SignupUseCase)[keyof typeof SignupUseCase];

export type SignupUseCasesResult = {
  /**
   * Raw wire values — a future taxonomy change may add strings beyond
   * `SignupUseCase`. Test membership against `SignupUseCase` literals (as
   * `hasAutomatedWorkflowsUseCase` does) so unrecognized values never
   * qualify: the cohort gate must fail closed.
   */
  useCases: readonly string[];
  /**
   * Always false today: the profile rides on the auth bootstrap, which every
   * caller already renders below (the onboarding layout holds on
   * `useAuthQuery`, home renders under `_authenticated`). Kept in the
   * contract so the gate's loading fold-in doesn't churn if this ever moves
   * to its own fetch.
   */
  isLoading: boolean;
};

/** The current account's signup use cases, read from the auth bootstrap. */
export function useSignupUseCases(): SignupUseCasesResult {
  const { account } = useAuth();
  return { useCases: account.signup_profile?.messaging_use_cases ?? [], isLoading: false };
}

/**
 * The essentials onboarding cohort is anyone who picked automated workflows
 * during signup — the signup role deliberately does not matter.
 */
export function hasAutomatedWorkflowsUseCase(useCases: readonly string[]): boolean {
  return useCases.includes(SignupUseCase.AutomatedWorkflows);
}
